import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Copy } from 'lucide-react';
import { cn } from '../../lib/utils';

interface ChatCopyButtonProps {
  content: string;
  className?: string;
}

/**
 * Copies a message. Hidden until the row is hovered or the button is focused —
 * the row sets `group`, and keyboard users get it back through focus-visible.
 */
const ChatCopyButton: React.FC<ChatCopyButtonProps> = ({ content, className }) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(content);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      aria-label={t(copied ? 'chat.copied' : 'chat.copyMessage')}
      className={cn(
        'grid size-6 shrink-0 place-items-center rounded text-muted-foreground',
        'opacity-0 transition-opacity duration-100 group-hover:opacity-100 focus-visible:opacity-100',
        'hover:bg-accent focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring',
        className,
      )}
    >
      {copied ? (
        <Check className="size-4 text-success" strokeWidth={2.2} />
      ) : (
        <Copy className="size-4" strokeWidth={1.8} />
      )}
    </button>
  );
};

export default ChatCopyButton;
